/**
 * CurriculumDetailEditor — shared editable wrapper over CurriculumDetailView. Owns
 * the placement add/edit modal (with the canonical-node TopicSelector), the inline
 * header-edit save, and the save/delete mutations, wired through an injected API
 * adapter so the admin console AND the teacher app drive the SAME editor against
 * their own endpoints (admin: any curriculum; teacher: their own).
 *
 * Curriculum-metadata editing is inline in the header (CurriculumDetailView). Row
 * add/edit uses the placement modal; row delete + curriculum delete route through a
 * themed confirm dialog (no native confirm()).
 */
import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Modal, Input, Select, Button, FormField } from '../components';
import { TopicSelector } from '../components/TopicSelector';
import { CurriculumDetailView } from './CurriculumDetailView';
import type { BookLevel, CanonModule, CurriculumInfo, CurriculumPlacement, ComplexityFactor } from './types';
import { COMPLEXITY_OPTIONS, estimateFirstPassHours } from './types';

/** Reader-facing wording for each density label. */
const COMPLEXITY_LABEL: Record<ComplexityFactor, string> = {
  'very-light': 'very light', light: 'light', medium: 'medium', dense: 'dense', 'very-dense': 'very dense',
};
import { gradeBoardFit, formatGradeBoards } from './gradeBoards';

const BOOK_LEVELS: BookLevel[] = ['unit', 'chapter', 'section'];

// Book hierarchy within a curriculum (NOT the canonical tree): a chapter nests
// only under a unit (or top level), a section only under a chapter, a subsection
// only under a section. Units are always top-level. `null` = no valid parent
// level → top-level only.
const BOOK_PARENT_LEVEL: Record<BookLevel, BookLevel | null> = {
  unit: null, chapter: 'unit', section: 'chapter', subsection: 'section',
};
// Levels that may also sit at the top with no parent.
const CAN_BE_TOP_LEVEL = new Set<BookLevel>(['unit', 'chapter']);
type CanonLevel = 'module' | 'chapter' | 'topic';
type Draft = Partial<CurriculumPlacement>;

export interface CurriculumApiAdapter {
  savePlacement: (curriculumId: string, draft: Partial<CurriculumPlacement>) => Promise<unknown>;
  deletePlacement: (curriculumId: string, placementId: string) => Promise<unknown>;
  saveCurriculum: (c: Partial<CurriculumInfo>) => Promise<unknown>;
  deleteCurriculum: (curriculumId: string) => Promise<unknown>;
}

export interface CurriculumEditorOptions {
  classes: string[];
  subjects: string[];
  targetExams: string[];
}

interface Props {
  curriculum: CurriculumInfo;
  placements: CurriculumPlacement[];
  modules: CanonModule[];
  questionCountByPlacement?: Record<string, number>;
  questionCountsByModule?: Record<string, number>;
  teacherQuestionCountByPlacement?: Record<string, number>;
  api: CurriculumApiAdapter;
  options: CurriculumEditorOptions;
  onChanged: () => void;   // parent refetches after a write
  onDeleted: () => void;   // parent navigates away after a curriculum delete
  backSlot?: React.ReactNode;
  headerNote?: React.ReactNode;
  /** Render the curriculum without any editing affordance. Used where the
   *  viewer may READ something they do not own — an admin looking at a
   *  teacher's curriculum — so the page shows the structure without offering
   *  edits that would rewrite someone else's work. */
  readOnly?: boolean;
  /** Passed straight through — see CurriculumDetailView. Teachers hide it. */
  showGradeBoards?: boolean;
}

export function CurriculumDetailEditor({
  curriculum, placements, modules, questionCountByPlacement, questionCountsByModule,
  teacherQuestionCountByPlacement,
  api, options, onChanged, onDeleted, backSlot, headerNote, readOnly, showGradeBoards,
}: Props) {
  const id = curriculum.id;
  const [placeModal, setPlaceModal] = useState<{ draft: Draft; isNew: boolean } | null>(null);
  const [confirmState, setConfirmState] = useState<{ message: string; onConfirm: () => void } | null>(null);

  const { byId, levelOf, pathOf } = useMemo(() => {
    const byId = new Map(modules.map(m => [m.id, m]));
    const levelOf = (m?: CanonModule): CanonLevel | undefined => (m ? (m.moduleLevel ?? (m.isTopModule ? 'module' : 'chapter')) : undefined);
    const pathOf = (m?: CanonModule): string => {
      if (!m) return '';
      const chain = [m.displayName]; let cur = m, hops = 0;
      while (cur.parentModuleId && hops < 3) {
        const p = byId.get(cur.parentModuleId); if (!p) break;
        chain.unshift(p.displayName); cur = p; hops++;
      }
      return chain.slice(0, -1).join(' › ');
    };
    return { byId, levelOf, pathOf };
  }, [modules]);

  const selectorModules = useMemo(() => modules.map(m => ({
    id: m.id, displayName: m.displayName, subject: m.subject,
    isTopModule: m.isTopModule ?? (m.moduleLevel === 'module'),
    moduleLevel: m.moduleLevel ?? 'chapter', parentModuleId: m.parentModuleId,
    sortOrder: m.sortOrder ?? 0, grades: m.grades,
  })), [modules]);

  const topLevel = useMemo(() => placements.filter(p => !p.parentId), [placements]);

  const savePlacement = useMutation({
    mutationFn: (p: Draft) => api.savePlacement(id, p),
    onSuccess: () => { onChanged(); setPlaceModal(null); },
  });
  const deletePlacement = useMutation({
    mutationFn: (placementId: string) => api.deletePlacement(id, placementId),
    onSuccess: () => { onChanged(); setConfirmState(null); },
  });
  const saveCurriculum = useMutation({
    mutationFn: (c: Partial<CurriculumInfo>) => api.saveCurriculum(c),
    onSuccess: () => onChanged(),
  });
  const deleteCurriculum = useMutation({
    mutationFn: () => api.deleteCurriculum(id),
    onSuccess: () => { setConfirmState(null); onDeleted(); },
  });

  const newChapterUnder = (unit: CurriculumPlacement) => {
    const siblings = placements.filter(p => p.parentId === unit.id);
    setPlaceModal({ isNew: true, draft: { bookLevel: 'chapter', parentId: unit.id, number: '', displayName: '', order: siblings.length } });
  };
  const newTopic = (chapter: CurriculumPlacement) => {
    const siblings = placements.filter(p => p.parentId === chapter.id);
    setPlaceModal({ isNew: true, draft: { bookLevel: 'section', parentId: chapter.id, number: `${chapter.number}.`, displayName: '', order: siblings.length } });
  };

  const d = placeModal?.draft;
  const canonNode = d?.canonicalNodeIds?.[0] ? byId.get(d.canonicalNodeIds[0]) : undefined;
  // Grade × Board of the node being mapped — read-only here (authored on the module),
  // but it decides which grade-curriculum the node's questions land in, so mapping a
  // node this board teaches at another grade is worth flagging at pick time.
  const canonFit = gradeBoardFit(canonNode?.gradeBoards, curriculum.board, curriculum.grade);
  const deleteErr = (deletePlacement.error || deleteCurriculum.error) as Error | null;

  const bookLevelById = useMemo(
    () => new Map(placements.map(p => [p.id, p.bookLevel])),
    [placements],
  );

  // Candidate parents for the entry being edited: placements at exactly the
  // parent book-level for this entry's level (chapter→unit, section→chapter),
  // minus the entry itself and its descendants (nesting under a descendant = a
  // cycle). Units have no valid parent level → empty (top-level only).
  const parentCandidates = useMemo(() => {
    const parentLevel = d?.bookLevel ? BOOK_PARENT_LEVEL[d.bookLevel] : null;
    if (!parentLevel) return [];
    const banned = new Set<string>();
    if (d?.id) {
      banned.add(d.id);
      for (let changed = true; changed;) {
        changed = false;
        for (const p of placements) {
          if (p.parentId && banned.has(p.parentId) && !banned.has(p.id)) { banned.add(p.id); changed = true; }
        }
      }
    }
    return placements
      .filter(p => p.bookLevel === parentLevel && !banned.has(p.id))
      .sort((a, b) => (a.number ?? '').localeCompare(b.number ?? '', undefined, { numeric: true }));
  }, [placements, d?.id, d?.bookLevel]);

  const canBeTopLevel = d?.bookLevel ? CAN_BE_TOP_LEVEL.has(d.bookLevel) : true;

  return (
    <CurriculumDetailView
      curriculum={curriculum}
      placements={placements}
      modules={modules}
      questionCountByPlacement={questionCountByPlacement}
      questionCountsByModule={questionCountsByModule}
      teacherQuestionCountByPlacement={teacherQuestionCountByPlacement}
      backSlot={backSlot}
      headerNote={headerNote}
      editOptions={options}
      showGradeBoards={showGradeBoards}
      edit={readOnly ? undefined : {
        onSaveCurriculum: (draft) => saveCurriculum.mutate(draft),
        onDeleteCurriculum: () => setConfirmState({ message: `Delete "${curriculum.name}" and all its placements?`, onConfirm: () => deleteCurriculum.mutate() }),
        onNewUnit: () => setPlaceModal({ isNew: true, draft: { bookLevel: 'unit', number: '', displayName: '', order: topLevel.length } }),
        onNewChapter: () => setPlaceModal({ isNew: true, draft: { bookLevel: 'chapter', number: '', displayName: '', order: topLevel.length } }),
        onNewChapterUnder: newChapterUnder,
        onNewTopic: newTopic,
        onEditRow: (p) => setPlaceModal({ isNew: false, draft: { ...p } }),
        onDeleteRow: (p) => setConfirmState({ message: 'Delete this entry (and any topics under it)?', onConfirm: () => deletePlacement.mutate(p.id) }),
      }}
    >
      {/* Placement editor — book entry + optional canonical mapping */}
      <Modal isOpen={!!placeModal} onClose={() => setPlaceModal(null)} title={placeModal?.isNew ? `Add ${d?.bookLevel ?? 'entry'}` : 'Edit entry'} size="sm">
        {d && (
          <div style={{ display: 'grid', gap: 12 }}>
            <FormField label={<>Label (printed, e.g. <code>1</code>, <code>1.1</code>, or <code>A</code>)</>}>
              <Input value={d.number ?? ''} onChange={(e) => setPlaceModal({ ...placeModal!, draft: { ...d, number: e.target.value } })} />
            </FormField>
            <FormField label="Title (verbatim from the textbook)">
              <Input value={d.displayName ?? ''} onChange={(e) => setPlaceModal({ ...placeModal!, draft: { ...d, displayName: e.target.value } })} />
            </FormField>
            <FormField label="Book level">
              <Select value={d.bookLevel ?? ''} onChange={(e) => {
                const bookLevel = e.target.value as BookLevel;
                // Drop the current parent if it no longer matches the new level's parent rule.
                const keep = d.parentId && bookLevelById.get(d.parentId) === BOOK_PARENT_LEVEL[bookLevel];
                setPlaceModal({ ...placeModal!, draft: { ...d, bookLevel, parentId: keep ? d.parentId : undefined } });
              }}>
                {BOOK_LEVELS.map(b => <option key={b} value={b}>{b}</option>)}
              </Select>
            </FormField>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <label style={{ margin: 0 }}>Canonical node mapping (optional)</label>
                {d.canonicalNodeIds?.length ? <button type="button" style={{ background: 'none', border: 'none', color: 'var(--color-info)', cursor: 'pointer', fontSize: 12 }}
                  onClick={() => setPlaceModal({ ...placeModal!, draft: { ...d, canonicalNodeIds: undefined } })}>Clear all</button> : null}
              </div>
              {/* Primary node ([0]) — search preserves any additional bundled nodes ([1..]). */}
              <TopicSelector
                modules={selectorModules}
                value={d.canonicalNodeIds?.[0] ?? ''}
                placeholder="Primary canonical node…"
                onChange={(moduleId) => {
                  const rest = (d.canonicalNodeIds?.slice(1) ?? []).filter(id => id !== moduleId);
                  const next = moduleId ? [moduleId, ...rest] : (rest.length ? rest : undefined);
                  setPlaceModal({ ...placeModal!, draft: { ...d, canonicalNodeIds: next } });
                }}
              />
              {canonNode && <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>primary: {levelOf(canonNode)} · {pathOf(canonNode) || '(top level)'}</div>}
              {canonNode && (
                <div style={{ fontSize: 11, marginTop: 2, color: canonFit.kind === 'otherGrade' ? 'var(--color-warning)' : 'var(--text-tertiary)' }}>
                  {canonFit.kind === 'otherGrade'
                    ? `⚠ Grade × Board: ${curriculum.board} teaches this at Class ${canonFit.grades.join(' / ')}, not Class ${curriculum.grade} — its questions count towards that curriculum.`
                    : `Grade × Board: ${formatGradeBoards(canonNode.gradeBoards) || '— none set (grade falls back to the question’s class)'}`}
                </div>
              )}

              {/* Additional canonical nodes ([1..]) as removable chips. */}
              {(d.canonicalNodeIds?.slice(1) ?? []).length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                  {(d.canonicalNodeIds?.slice(1) ?? []).map(id => {
                    const m = byId.get(id);
                    const label = m ? `${m.displayName}${pathOf(m) ? ` · ${pathOf(m)}` : ''}` : id;
                    return (
                      <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '2px 8px', borderRadius: 999, background: 'var(--bg-tertiary, #2a2a35)', color: 'var(--text-primary)' }}>
                        {label}
                        <button type="button" title="Remove mapping" style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}
                          onClick={() => setPlaceModal({ ...placeModal!, draft: { ...d, canonicalNodeIds: (d.canonicalNodeIds ?? []).filter(x => x !== id) } })}>×</button>
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Add another canonical node — appends to [1..]. Only once a primary exists. */}
              {d.canonicalNodeIds?.[0] && (
                <div style={{ marginTop: 8 }}>
                  <TopicSelector
                    modules={selectorModules}
                    value=""
                    placeholder="Add another canonical node…"
                    onChange={(moduleId) => {
                      if (!moduleId) return;
                      const cur = d.canonicalNodeIds ?? [];
                      if (cur.includes(moduleId)) return;   // no duplicates
                      setPlaceModal({ ...placeModal!, draft: { ...d, canonicalNodeIds: [...cur, moduleId] } });
                    }}
                  />
                </div>
              )}
            </div>
            <FormField label="Parent (move in hierarchy)">
              <Select
                value={d.parentId ?? ''}
                disabled={d.bookLevel === 'unit'}
                onChange={(e) => setPlaceModal({ ...placeModal!, draft: { ...d, parentId: e.target.value || undefined } })}
              >
                {canBeTopLevel && <option value="">— None (top level) —</option>}
                {parentCandidates.map(p => (
                  <option key={p.id} value={p.id}>
                    {[p.number, p.displayName].filter(Boolean).join(' ')}
                  </option>
                ))}
              </Select>
              {d.bookLevel === 'unit'
                ? <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>Units are always top-level.</div>
                : !canBeTopLevel && parentCandidates.length === 0
                  ? <div style={{ fontSize: 11, color: 'var(--color-error)', marginTop: 2 }}>No {BOOK_PARENT_LEVEL[d.bookLevel ?? 'section']} to nest under — create one first.</div>
                  : <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>A {d.bookLevel} can sit under a {BOOK_PARENT_LEVEL[d.bookLevel ?? 'section']}{canBeTopLevel ? ' or at the top level' : ''}.</div>}
            </FormField>
            <FormField label="Order within parent">
              <Input type="number" value={d.order ?? ''} onChange={(e) => setPlaceModal({ ...placeModal!, draft: { ...d, order: e.target.value === '' ? undefined : Number(e.target.value) } })} />
            </FormField>
            {/* Effort inputs — chapters only. A unit is a grouping; its effort is the
                sum of its chapters, so asking for pages here would double-count. */}
            {d.bookLevel === 'chapter' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 8 }}>
                <FormField label="Pages in book">
                  <Input
                    type="number" min={1} placeholder="e.g. 37"
                    value={d.pageCount ?? ''}
                    onChange={(e) => setPlaceModal({ ...placeModal!, draft: { ...d, pageCount: e.target.value === '' ? undefined : Number(e.target.value) } })}
                  />
                </FormField>
                <FormField label="Complexity">
                  <Select
                    value={d.complexityFactor ?? ''}
                    onChange={(e) => setPlaceModal({ ...placeModal!, draft: { ...d, complexityFactor: (e.target.value || undefined) as CurriculumPlacement['complexityFactor'] } })}
                  >
                    <option value="">— Medium (default) —</option>
                    {COMPLEXITY_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label} — {o.hint}</option>
                    ))}
                  </Select>
                </FormField>
                <div style={{ gridColumn: '1 / -1', fontSize: 11, color: 'var(--text-secondary)', marginTop: -2 }}>
                  {d.pageCount
                    ? <>≈ <strong>{estimateFirstPassHours(d.pageCount, d.complexityFactor)} h</strong> for a first read
                        {' '}({d.pageCount} pp × {COMPLEXITY_LABEL[d.complexityFactor ?? 'medium']}). Revisions cost less.</>
                    : <>Pages come from the book&apos;s own contents page. Without them this chapter cannot be sized, and a schedule will say so rather than assume it is free.</>}
                </div>
              </div>
            )}
            {savePlacement.error ? <div style={{ color: 'var(--color-error)', fontSize: 13 }}>{(savePlacement.error as Error).message}</div> : null}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="secondary" size="sm" onClick={() => setPlaceModal(null)}>Cancel</Button>
              <Button variant="primary" size="sm" disabled={!d.number || !d.displayName || !d.bookLevel || (!canBeTopLevel && !d.parentId) || savePlacement.isPending}
                onClick={() => savePlacement.mutate(d)}>Save</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Themed confirm dialog (replaces native confirm) */}
      <Modal isOpen={!!confirmState} onClose={() => setConfirmState(null)} title="Please confirm" size="sm">
        {confirmState && (
          <div style={{ display: 'grid', gap: 14 }}>
            <p style={{ margin: 0 }}>{confirmState.message}</p>
            {deleteErr ? <div style={{ color: 'var(--color-error)', fontSize: 13 }}>{deleteErr.message}</div> : null}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="secondary" size="sm" onClick={() => setConfirmState(null)}>Cancel</Button>
              <Button variant="danger" size="sm" disabled={deletePlacement.isPending || deleteCurriculum.isPending} onClick={confirmState.onConfirm}>Delete</Button>
            </div>
          </div>
        )}
      </Modal>
    </CurriculumDetailView>
  );
}
